<template>
    <div class="container">
        <div class="row">
            <div class="col-12 col-md-6 pt-4">
                <emblem>{{ name }}</emblem>
            </div>
            <div class="col-12 col-md-6 pt-4">
                <label class="toggle-switch float-right">
                    <span class="mr-2 font-weight-bold"
                        >Only show followed subjects</span
                    >
                    <input
                        v-model="selectedFilters.showFollowedSubjects"
                        class="toggle-switch__input"
                        type="checkbox"
                        :disabled="loading"
                    />
                    <span class="toggle-switch__slider mr-0"></span>
                </label>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <div v-if="loading" class="row">
                    <div class="col text-center mt-3 mb-3">
                        <div class="emblem emblem--loader"></div>
                    </div>
                </div>

                <div v-if="!loading && error" class="col text-center">
                    <p>
                        Er ging iets mis bij het ophalen van de gegevens.
                        Probeer het later opnieuw.
                    </p>
                </div>

                <div
                    v-if="
                        !loading &&
                            !error &&
                            (searchResults === null || !searchResults.length)
                    "
                    class="col text-center"
                >
                    <p>{{ translations.emptyResult }}</p>
                </div>
                <v-client-table
                    ref="startOfResults"
                    :columns="columns"
                    :options="options"
                    :data="tableData"
                    @row-click="onRowClick"
                >
                    <template v-slot:beforeTable>
                        <div class="legislation-monitor__table">
                            <div class="legislation-monitor__filter mr-md-2">
                                <select-input
                                    v-model="selectedFilters.selectedSubject"
                                    :label="'Themes'"
                                    :options="filters.subjects"
                                />
                            </div>
                            <div class="legislation-monitor__filter mr-md-2">
                                <select-input
                                    v-model="
                                        selectedFilters.selectedRecentChanges
                                    "
                                    :label="'Recent changes'"
                                    :options="filters.recentChanges"
                                />
                            </div>
                            <div class="legislation-monitor__filter mr-md-2">
                                <select-input
                                    v-model="selectedFilters.selectedStage"
                                    :label="'Stage'"
                                    :options="filters.stages"
                                />
                            </div>
                            <div class="legislation-monitor__filter mr-md-2">
                                <select-input
                                    v-model="selectedFilters.selectedLevel"
                                    :label="'Level'"
                                    :options="filters.processLevels"
                                />
                            </div>
                            <div class="legislation-monitor__filter mb-md-2">
                                <select-input
                                    v-model="selectedFilters.selectedType"
                                    :label="'Type'"
                                    :options="filters.types"
                                />
                            </div>
                            <div class="flex-break"></div>
                            <text-input
                                v-model="selectedFilters.searchQuery"
                                name="search"
                                class="search mb-2 mb-md-0 mr-md-2"
                                label="Search subjects or legislation"
                            ></text-input>
                            <span class="reset mt-2" @click="resetFilters"
                                >Reset filters <i class="fas fa-times"></i
                            ></span>
                        </div>
                    </template>
                </v-client-table>
                <paging
                    v-model="currentPage"
                    :total-pages="totalPages"
                    :disabled="loading"
                    @input="scrollToResults"
                />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Vue from 'vue';
import VueTables from 'vue-tables-2';
import emblem from '@/components/emblem';
import textInput from '@/components/form/text-input';
import selectInput from '@/components/form/select-input';
import paging from '@/components/navigation/paging';
import overview from '@/mixins/overview.js';
import transition_staggered_fade from '@/mixins/transition-staggered-fade-mixin.js';
import translationMixin from '@/mixins/translations';

Vue.use(VueTables.ClientTable);

export default {
    components: {
        emblem,
        textInput,
        selectInput,
        paging
    },
    mixins: [overview, transition_staggered_fade, translationMixin],
    props: {
        filters: {
            type: Object,
            default() {
                return {};
            }
        },
        name: {
            type: String,
            default: 'Legislation Monitor'
        }
    },
    data() {
        return {
            currentPage: 1,
            totalPages: 1,
            error: false,
            columns: ['code', 'name', 'stage', 'level', 'type'],
            tableData: [],
            params: '',
            selectedFilters: {
                selectedSubject: '',
                selectedRecentChanges: '',
                selectedStage: '',
                selectedLevel: '',
                selectedType: '',
                searchQuery: '',
                showFollowedSubjects: false
            },
            options: {
                filterable: false,
                perPage: 10,
                perPageValues: [10],
                pagination: {
                    show: false
                }
            }
        };
    },
    watch: {
        selectedFilters: {
            deep: true,
            handler() {
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }
                this.timeout = setTimeout(() => {
                    this.getItems();
                }, 400);
            }
        }
    },
    created() {
        this.getSubjectParams();
        this.getSearchParams();
        this.getFollowingParams();
    },
    methods: {
        getSubjectParams() {
            const subjectParams = this.$route.query.subject;
            if (subjectParams === undefined) {
                return false;
            } else {
                this.selectedFilters.selectedSubject = subjectParams;
            }
        },
        getSearchParams() {
            const searchParams = this.$route.query.search;
            if (searchParams === undefined) {
                return false;
            } else {
                this.selectedFilters.searchQuery = searchParams;
            }
        },
        getFollowingParams() {
            const searchParams = this.$route.query.following;
            if (searchParams === undefined) {
                return false;
            } else {
                this.selectedFilters.showFollowedSubjects =
                    searchParams === 'true';
            }
        },
        getItems() {
            clearInterval(this.elapsed);
            this.loading = true;
            const getUrl =
                '/umbraco/api/legislation/legislations?' +
                'currentPage=' +
                this.$root.settings.currentPage +
                '&subject=' +
                this.selectedFilters.selectedSubject +
                '&recentChanges=' +
                this.selectedFilters.selectedRecentChanges +
                '&stage=' +
                this.selectedFilters.selectedStage +
                '&level=' +
                this.selectedFilters.selectedLevel +
                '&page=' +
                this.currentPage +
                '&type=' +
                this.selectedFilters.selectedType +
                '&searchTerm=' +
                this.selectedFilters.searchQuery +
                '&showOnlyFollowedSubjects=' +
                this.selectedFilters.showFollowedSubjects;

            axios
                .get(getUrl)
                .then(response => {
                    if (response.data !== null) {
                        this.tableData = response.data.items;
                        this.totalPages = response.data.totalPages;
                    }
                })
                .catch(() => {
                    this.error = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        scrollToResults() {
            const element = this.$refs['startOfResults'];
            window.scrollTo({
                top: element.offsetTop - 300,
                behavior: 'smooth'
            });
        },
        onRowClick(event) {
            window.open(event.row.url, '_blank');
        },
        resetFilters() {
            (this.selectedFilters.selectedSubject = ''),
                (this.selectedFilters.selectedRecentChanges = ''),
                (this.selectedFilters.selectedStage = ''),
                (this.selectedFilters.selectedLevel = ''),
                (this.selectedFilters.selectedType = ''),
                (this.selectedFilters.searchQuery = '');
        }
    }
};
</script>

<style lang="scss">
.legislation-monitor__table {
    margin-bottom: 2rem;

    @include media-breakpoint-up(md) {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: center;
    }
}

.flex-break {
    flex-basis: 100%;
    height: 0;
}

.table-responsive {
    overflow-x: auto;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
    border-bottom: 5px solid $brand-blue;
}

.legislation-monitor__filter {
    margin-bottom: 1rem;

    @include media-breakpoint-up(md) {
        margin-bottom: 0;
        flex: 1 1 auto;
    }
}

.legislation-monitor__filter select {
    width: 100%;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
}

.legislation-monitor__filter {
    position: relative;

    &:after {
        font-family: 'icomoon' !important;
        speak: none;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: $brand-blue;
        font-weight: bold;
        font-size: 1.25rem;
        pointer-events: none;
        content: '\f107';
    }
}

.search {
    flex: 1 1 415px;
    position: relative;

    &:after {
        font-family: 'icomoon' !important;
        speak: none;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: $brand-blue;
        content: '\f002';
    }
}

.reset {
    flex: 0 1 auto;
    justify-self: flex-end;
    text-align: right;
    color: $green;
    font-weight: bold;
    cursor: pointer;
}

.VueTables__table {
    width: 100%;
    background-color: $brand-white;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
}

.VueTables__table tr {
    border-bottom: 1px solid $brand-blue;
}

.VueTables__table tr.VueTables__row {
    &:hover {
        background-color: $brand-gray;
    }
}

.VueTables__row {
    cursor: pointer;
}

.VueTables__row td:first-of-type {
    font-weight: $font-weight-bold;
}

.VueTables__table td {
    padding: 10px 14px;
    font-size: 0.85rem;
    border-right: 1px solid $brand-gray;

    @include media-breakpoint-up(lg) {
        font-size: 1rem;
        padding: 10px 24px;
    }
}

.VueTables__table th {
    padding: 10px 24px;
    color: $brand-gray-dark;
    border-right: 1px solid $brand-gray;

    &:after {
        position: relative;
        font-family: 'icomoon' !important;
        speak: none;
        left: 10px;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        font-weight: 700;
        font-size: 0.8rem;

        /* Better Font Rendering =========== */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        content: '\f107';
    }
}

.VueTables__table th[class*='-sorted-asc'] {
    &:after {
        position: relative;
        font-family: 'icomoon' !important;
        speak: none;
        left: 10px;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        font-weight: 700;
        font-size: 0.8rem;

        /* Better Font Rendering =========== */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        content: '\f106';
    }
}
</style>
